import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { 
  Scale, 
  DollarSign, 
  Building2, 
  ShieldCheck, 
  UserCheck,
  Clock,
  Heart,
  Lock,
  Users,
  Banknote
} from 'lucide-react';

const chapters = [
  {
    id: 'chapter-01',
    number: '01',
    title: 'Employment & Earnings Structure',
    subtitle: 'Contractor Model & Income Splits',
    description: 'Most brothels in Melbourne operate a "contractor model." Instead of a monthly salary or hourly wages, you\'re paid for the work you do per client—similar to legal professionals or graphic designers being paid per project.',
    content: [
      {
        icon: DollarSign,
        title: 'Service Fee Split System',
        text: 'Clients pay a fee for services, split between you and the venue. In most cases, you\'ll retain a significant majority share. Any fee you pay goes towards staff wages and facility maintenance.'
      },
      {
        icon: Banknote,
        title: 'Direct Client Arrangements',
        text: 'You\'re free to arrange services with clients directly outside of the brothel environment. Always check terms of service rules regarding the brothel\'s existing clients.'
      },
      {
        icon: Clock,
        title: 'Flexibility & Scheduling',
        text: 'As a contractor, you have full flexibility over your scheduling. Most venues let you decide when you meet clients, allowing you to work around other responsibilities or day jobs.'
      }
    ],
    image: 'https://sydneybrothelsguide.com.au/images/atmc-the-penthouse.jpg'
  },
  {
    id: 'chapter-02',
    number: '02',
    title: 'Venue Operations & Room Architecture',
    subtitle: 'Professional Establishment Standards',
    description: 'Physical establishments are similar to other places of work in terms of how they operate. Most brothels operate similarly to conventional corporate structures, providing security, reception teams, and internal frameworks.',
    content: [
      {
        icon: Users,
        title: 'Reception Support',
        text: 'Receptionists handle administrative tasks—answering client queries, screening calls, managing who visits you. This is different from independent work where you manage everything yourself.'
      },
      {
        icon: Building2,
        title: 'Physical Amenities',
        text: 'Linen services, ensuite bathrooms, climate control, soundproofing for privacy, and on-site security. Rest areas with TVs, internet, and drinks machines between bookings.'
      },
      {
        icon: Heart,
        title: 'Venue Maintenance',
        text: 'Management is responsible for maintaining and cleaning everything. It\'s not an additional logistical burden for you—you focus on your work.'
      }
    ],
    image: 'https://sydneybrothelsguide.com.au/images/stiletto-presidential-suite.jpg'
  },
  {
    id: 'chapter-03',
    number: '03',
    title: 'Workplace Culture & Safety',
    subtitle: 'Client Screening & Your Rights',
    description: 'Professional venues have robust management ensuring everyone is protected, including you. You always have the absolute right to refuse a client—venues cannot force you to take on someone you don\'t want to receive.',
    content: [
      {
        icon: ShieldCheck,
        title: 'Reliable Pre-Screening',
        text: 'Professional venues filter out potentially dangerous or unpleasant clients before you see them. Brothels assess sobriety and screen out anyone violating house rules.'
      },
      {
        icon: Lock,
        title: 'On-Site Security Measures',
        text: 'Monitoring systems at entry points and hallways. Discreetly positioned panic buttons in every room. Internal communication systems alert colleagues if there\'s any issue.'
      },
      {
        icon: Heart,
        title: 'Healthcare Control',
        text: 'Under Victoria public health guidelines, you have full control over your healthcare and personal safety choices. You decide barriers and boundaries with clients.'
      }
    ],
    image: 'https://sydneybrothelsguide.com.au/images/ga-room-7.jpg'
  },
  {
    id: 'chapter-04',
    number: '04',
    title: 'Pre-Entry Considerations',
    subtitle: 'What to Know Before You Start',
    description: 'Before entering the industry and getting a brothel job in Melbourne, there are important considerations. While laws and regulations protect sex workers, it\'s critical to choose the right venue and understand the financial aspects.',
    content: [
      {
        icon: Building2,
        title: 'Venue Choice',
        text: 'Not every establishment is the same. Check the culture and house rules. Ensure you receive a fair income share. Read reviews and get referrals from existing workers.'
      },
      {
        icon: DollarSign,
        title: 'Financial Literacy',
        text: 'Think long term about income tax and expenses. Many recommend treating it like a small business—manage your time, income, and expenses to generate profit.'
      },
      {
        icon: UserCheck,
        title: 'Privacy & Identity',
        text: 'Consider social stigma associated with the industry. Generally, use pseudonyms and dedicated work communication channels to protect your identity and privacy.'
      }
    ],
    image: 'https://sydneybrothelsguide.com.au/images/liaisons-brothel-deluxe-suite.jpg'
  },
  {
    id: 'chapter-05',
    number: '05',
    title: 'Decriminalisation & Legal Framework',
    subtitle: 'Sex Work Decriminalisation Act Victoria',
    description: 'Victoria completely decriminalised sex work recently and transitioned away from the old licensing system and heavy-handed state surveillance. The industry now operates under "mainstream economic regulation," meaning you\'re free to work in the sector just as you might in hospitality, finance, or telecommunications.',
    content: [
      {
        icon: Scale,
        title: 'Occupational Health & Safety',
        text: 'Brothels must operate according to OHS standards, just like other industries. Venues have a legal requirement to provide a safe and hygienic workplace, well-maintained physical working environment.'
      },
      {
        icon: ShieldCheck,
        title: 'Anti-Discrimination Protections',
        text: 'Under Victoria\'s Equal Opportunity Act, sex work is a protected attribute. Employers can\'t discriminate against you if they discover you are or were a sex worker. You cannot be denied housing or financial services.'
      },
      {
        icon: Lock,
        title: 'Privacy & Banking Access',
        text: 'This legislation means you can work in a parlour without worrying about it affecting your livelihood or ability to use conventional banking systems.'
      }
    ],
    image: 'https://sydneybrothelsguide.com.au/images/liaisons-brothel-the-penthouse-suite.jpg'
  }
];

const ChapterCard = ({ chapter, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.article
      ref={ref}
      id={chapter.id}
      className="py-20 lg:py-32"
      initial={{ opacity: 0 }}
      animate={isInView ? { opacity: 1 } : { opacity: 0 }}
      transition={{ duration: 0.8 }}
      data-testid={chapter.id}
    >
      <div className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-start ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
        {/* Content Side */}
        <motion.div
          className={`space-y-8 ${index % 2 === 1 ? 'lg:order-2' : ''}`}
          initial={{ opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? -40 : 40 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Chapter Label */}
          <div className="flex items-center gap-4">
            <span className="chapter-number">Chapter {chapter.number}</span>
            <div className="h-px bg-[#E02852] w-16" />
          </div>

          {/* Title */}
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F7F6F2] tracking-tight leading-tight mb-3">
              {chapter.title}
            </h2>
            <p className="font-mono text-sm text-[#D4AF37] tracking-wide uppercase">
              {chapter.subtitle}
            </p>
          </div>

          {/* Description */}
          <p className="text-base sm:text-lg text-[#A1A1AA] leading-relaxed">
            {chapter.description}
          </p>

          {/* Content Cards */}
          <div className="space-y-4 pt-4">
            {chapter.content.map((item, itemIndex) => (
              <motion.div
                key={item.title}
                className="chapter-card p-6 rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.4 + itemIndex * 0.1 }}
              >
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[rgba(224,40,82,0.15)] flex items-center justify-center">
                    <item.icon size={20} className="text-[#E02852]" />
                  </div>
                  <div>
                    <h3 className="font-medium text-[#F7F6F2] mb-2">{item.title}</h3>
                    <p className="text-sm text-[#A1A1AA] leading-relaxed">{item.text}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Image Side */}
        <motion.div
          className={`relative ${index % 2 === 1 ? 'lg:order-1' : ''}`}
          initial={{ opacity: 0, x: index % 2 === 0 ? 40 : -40 }}
          animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: index % 2 === 0 ? 40 : -40 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden">
            <img
              src={chapter.image}
              alt={chapter.title}
              className="w-full h-full object-cover"
            />
            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-transparent to-transparent opacity-60" />
            
            {/* Chapter number overlay */}
            <div className="absolute bottom-6 left-6 font-serif text-7xl lg:text-8xl text-[#F7F6F2] opacity-10 font-light">
              {chapter.number}
            </div>
          </div>

          {/* Decorative elements */}
          <div className="absolute -z-10 top-8 -right-8 w-full h-full rounded-2xl border border-[rgba(224,40,82,0.2)]" />
        </motion.div>
      </div>
    </motion.article>
  );
};

const ManifestoChapters = () => {
  return (
    <section className="relative bg-[#0A0A0C]" data-testid="manifesto-chapters">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="py-20 text-center">
          <motion.span
            className="font-mono text-xs text-[#E02852] tracking-[0.3em] uppercase block mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            Industry Guide
          </motion.span>
          <motion.h2
            className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#F7F6F2] tracking-tight"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
          >
            Everything You Need to Know
          </motion.h2>
        </div>

        {/* Chapters */}
        <div className="divide-y divide-[rgba(255,255,255,0.06)]">
          {chapters.map((chapter, index) => (
            <ChapterCard key={chapter.id} chapter={chapter} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ManifestoChapters;
